import { isIOS } from 'shared-utils/device-utils'
import { isValidDateRange, sortDateStrings, filterSelectableDates } from 'shared-utils/date-utils'

/**
 * Shared datepicker utility functions used by both Elements and React packages.
 * Only framework-agnostic functions belong here.
 */

/**
 * Determines the appropriate input type based on device.
 * Returns 'text' on iOS to avoid native date picker conflicts.
 */
export const getDatepickerInputType = (): string => {
  return isIOS() ? 'text' : 'date'
}

/**
 * Validates that a range has valid order (start <= end).
 * Returns true if range is valid or incomplete.
 */
export const validateRangeOrder = (values: string[]): boolean => {
  if (!values || values.length !== 2) return true
  return isValidDateRange(values[0], values[1])
}

/**
 * Sorts date strings chronologically.
 */
export const sortDates = (dates: string[]): string[] => {
  return sortDateStrings(dates)
}

/**
 * Filters dates to only include selectable ones based on constraints.
 */
export const filterDates = (
  dates: string[],
  min?: string | null,
  max?: string | null,
  excludedDates?: string[],
  excludedWeekdays?: string[],
): string[] => {
  return filterSelectableDates(dates, min, max, excludedDates, excludedWeekdays)
}

/**
 * Generates input CSS classes for datepicker.
 */
export const getDatepickerInputClasses = (
  fullwidth: boolean,
  showRangeLabels: boolean,
  multiple: boolean,
  range: boolean,
  readonly?: boolean,
  inputType?: string,
): Record<string, boolean> => {
  return {
    'pkt-input': true,
    'pkt-datepicker__input': true,
    'pkt-input--fullwidth': fullwidth,
    'pkt-datepicker--hasrangelabels': showRangeLabels,
    'pkt-datepicker--multiple': multiple,
    'pkt-datepicker--range': range,
    'ios-readonly-hack': readonly === false && inputType === 'text',
  }
}

/**
 * Generates button CSS classes for calendar toggle button.
 */
export const getDatepickerButtonClasses = (): Record<string, boolean> => {
  return {
    'pkt-input-icon': true,
    'pkt-btn': true,
    'pkt-btn--icon-only': true,
    'pkt-btn--tertiary': true,
    'pkt-datepicker__calendar-button': true,
  }
}

/**
 * Generates range label CSS classes.
 */
export const getRangeLabelClasses = (showRangeLabels: boolean): Record<string, boolean> => {
  return {
    'pkt-input-prefix': showRangeLabels,
    'pkt-hide': !showRangeLabels,
  }
}

/**
 * Processes date selection from calendar events.
 * Converts the calendar's date selection into the appropriate string format.
 */
export const processDateSelection = (
  detail: string | string[],
  multiple: boolean,
  range: boolean,
): string => {
  if (!multiple && !range) {
    return Array.isArray(detail) ? detail[0] || '' : detail
  }
  if (Array.isArray(detail)) {
    return detail.join(',')
  }
  return detail
}

/**
 * Handles calendar positioning based on viewport and input position.
 * Positions calendar below input by default, flips above if overflowing.
 *
 * Accepts plain DOM elements (not framework-specific refs).
 */
export const handleCalendarPosition = (
  popup: HTMLElement | null,
  input: HTMLElement | null,
  hasCounter: boolean = false,
): void => {
  if (!popup || !input) return

  const inputRect = input.parentElement?.getBoundingClientRect() || input.getBoundingClientRect()
  const inputHeight = hasCounter ? inputRect.height + 30 : inputRect.height
  const popupHeight = popup.getBoundingClientRect().height

  let top = hasCounter ? 'calc(100% - 30px)' : '100%'

  if (
    inputRect &&
    inputRect.top + popupHeight > window.innerHeight &&
    inputRect.top - popupHeight > 0
  ) {
    top = `calc(100% - ${inputHeight}px - ${popupHeight}px)`
  }

  popup.style.top = top
}

/**
 * Handles common keyboard interactions for datepicker inputs.
 *
 * - Space: Opens calendar
 * - Enter: Submits form, focuses next input, or blurs
 * - Comma: Adds date to selection (multiple) or blurs input
 */
export const handleDatepickerKeydown = (
  event: KeyboardEvent,
  toggleCalendar: (e: Event) => void,
  submitForm?: () => void,
  focusNextInput?: () => void,
  blurInput?: () => void,
  commaHandler?: (e: KeyboardEvent) => void,
): void => {
  const { key } = event

  if (key === ',') {
    event.preventDefault()
    if (commaHandler) {
      commaHandler(event)
    } else if (blurInput) {
      blurInput()
    }
  }

  if (key === 'Space' || key === ' ') {
    event.preventDefault()
    toggleCalendar(event)
  }

  if (key === 'Enter') {
    event.preventDefault()
    if (submitForm) {
      submitForm()
    } else if (focusNextInput) {
      focusNextInput()
    } else if (blurInput) {
      blurInput()
    }
  }
}

/**
 * Handles keyboard interactions for calendar button.
 * Responds to Enter and Space keys.
 */
export const handleDatepickerButtonKeydown = (
  event: KeyboardEvent,
  toggleCalendar: (e: Event) => void,
): void => {
  const { key } = event
  if (key === 'Enter' || key === ' ' || key === 'Space') {
    event.preventDefault()
    toggleCalendar(event)
  }
}

export default {
  getDatepickerInputType,
  validateRangeOrder,
  sortDates,
  filterDates,
  getDatepickerInputClasses,
  getDatepickerButtonClasses,
  getRangeLabelClasses,
  processDateSelection,
  handleCalendarPosition,
  handleDatepickerKeydown,
  handleDatepickerButtonKeydown,
}
